import React, { useState, useEffect } from 'react';
import { TrendingUp, Search } from 'lucide-react';

  const InfrastructureTracker = () => {
  const [projects, setProjects] = useState([]);
  const [apiStats, setApiStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [filterState, setFilterState] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('investment');

  useEffect(() => {
    fetch('https://pivotintel.org/pivotintel/api/infrastructure.php')
      .then(res => {
        if (!res.ok) throw new Error('Failed to load data');
        return res.json();
      })
      .then(data => {
        console.log('API Response:', data);
        console.log('Projects array:', data.projects);
        console.log('Total investment from API:', data.total_investment);
      setApiStats(data);
      setProjects(data.projects || []);
      setLoading(false);
      })
      
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  const filteredData = projects
    .filter(p => {
      const matchesSearch = !search || 
        p.company.toLowerCase().includes(search.toLowerCase()) ||
        p.project_name.toLowerCase().includes(search.toLowerCase()) ||
        (p.location_city && p.location_city.toLowerCase().includes(search.toLowerCase()));
      const matchesState = filterState === 'all' || p.location_state === filterState;
      const matchesStatus = filterStatus === 'all' || p.status === filterStatus;
      return matchesSearch && matchesState && matchesStatus;
    })
    .sort((a, b) => {
      if (sortBy === 'investment') return (b.investment_announced || 0) - (a.investment_announced || 0);
      if (sortBy === 'date') return new Date(b.announced_date) - new Date(a.announced_date);
      if (sortBy === 'company') return a.company.localeCompare(b.company);
      return 0;
    });

  const totalInvestment = apiStats?.total_investment || 0;
  const statesCount = apiStats?.states_covered || 0;
  const operationalCount = apiStats?.operational_count || 0;
  const statesList = [...new Set(projects.map(p => p.location_state).filter(Boolean))].sort();
  const statuses = [...new Set(projects.map(p => p.status).filter(Boolean))];

if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-16 text-center">
        <div className="text-gray-600">Loading infrastructure projects...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-16 text-center">
        <div className="text-red-600">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-blue-100 p-3 rounded-lg">
            <TrendingUp className="h-8 w-8 text-blue-600" />
          </div>
          <div>
            <h1 className="text-4xl font-bold">Infrastructure Projects Tracker</h1>
            <p className="text-gray-600">Data center projects nationwide</p>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="text-3xl font-bold text-blue-600 mb-2">
            {projects.length}
          </div>
          <div className="text-sm text-gray-600">Projects Tracked</div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="text-3xl font-bold text-blue-600 mb-2">
            ${(totalInvestment / 1000000000).toFixed(0)}B
          </div>
          <div className="text-sm text-gray-600">Total Investment</div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="text-3xl font-bold text-blue-600 mb-2">
            {statesCount}
        </div>
        <div className="text-sm text-gray-600">States Covered</div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="text-3xl font-bold text-blue-600 mb-2">
            {operationalCount}
          </div>
          <div className="text-sm text-gray-600">Operational</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search projects..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <select
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            value={filterState}
            onChange={(e) => setFilterState(e.target.value)}
          >
            <option value="all">All States</option>
            {statesList.map(state => (
              <option key={state} value={state}>{state}</option>
            ))}
          </select>

          <select
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">All Statuses</option>
            {statuses.map(status => (
              <option key={status} value={status}>
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </option>
            ))}
          </select>

          <select
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="investment">Sort by Investment</option>
            <option value="date">Sort by Date</option>
            <option value="company">Sort by Company</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Company
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Project
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Location
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Investment
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredData.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                    No results found. Try adjusting your filters.
                  </td>
                </tr>
              ) : (
                filteredData.map((project) => (
                  <tr key={project.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-medium text-gray-900">{project.company}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{project.project_name || 'Unnamed'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {project.location_city ? `${project.location_city}, ` : ''}{project.location_state}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {project.investment_announced ? 
                        `$${(project.investment_announced / 1000000000).toFixed(1)}B` : 
                        'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        project.status === 'operational' ? 'bg-green-100 text-green-800' :
                        project.status === 'construction' ? 'bg-yellow-100 text-yellow-800' :
                        project.status === 'announced' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {project.status}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-4 text-sm text-gray-600">
        Showing {filteredData.length} of {projects.length} projects | 
        Last updated: {new Date().toLocaleDateString('en-US', { 
          month: 'long', 
          day: 'numeric', 
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit'
        })}
      </div>
    </div>
  );
};

export default InfrastructureTracker;